/**
 * CampaignProgressTab
 *
 * Brand-only view of every creator on a brand campaign:
 *  - Summary tiles
 *  - Status filter chips + Mandate filter chips (combine)
 *  - Per-creator status rows with mandate tags + deliverable + address chips
 *  - "Export Addresses" CSV download
 *  - Read-only address viewer
 *  - Mandates section (Add / Edit / Delete via shared MandatesPanel)
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Download, MapPin, Users, Loader2, CheckCircle2, Clock, AlertCircle, Package as PackageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MandatesPanel, MandateTag, mandatesForCreator } from '../MandatesPanel';
import { BrandCampaignReportPanel } from './BrandCampaignReportPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLOR = {
  invited: '#71717a', pitched: '#0055FF', outreach: '#9B6EFF',
  interested: '#0055FF', no_response: '#52525b', no_response_yet: '#52525b',
  rejected: '#FF3B30', declined: '#FF3B30',
  accepted: '#00C972', approved_by_brand: '#FFB800',
  product_shipped: '#A855F7', product_delivered: '#EC4899',
  draft_submitted: '#06B6D4', revision_required: '#F97316',
  approved: '#84CC16', posted: '#22C55E',
  locked: '#00C972', live: '#FF451A', completed: '#00C972',
};

const DELIV_META = {
  pending:   { label: 'Pending',   color: '#FFB800', Icon: Clock },
  submitted: { label: 'Submitted', color: '#06B6D4', Icon: AlertCircle },
  approved:  { label: 'Approved',  color: '#00C972', Icon: CheckCircle2 },
};

// Dispatch Tracker chip metadata — mirrors CM view colors.
const DISPATCH_META = {
  address_collected:  { label: 'Address Collected',  color: '#FF9500' },
  product_dispatched: { label: 'Product Dispatched', color: '#0055FF' },
  product_received:   { label: 'Product Received',   color: '#00C972' },
  content_pending:    { label: 'Content Pending',    color: '#FFB800' },
  not_applicable:     { label: 'N/A',                color: '#71717a' },
};

// Maps the filter chip → set of raw row statuses that count.
const STATUS_FILTERS = [
  { key: 'all',       label: 'All',       match: () => true },
  { key: 'invited',   label: 'Invited',   match: (s) => ['invited', 'pitched', 'outreach'].includes(s) },
  { key: 'responded', label: 'Responded', match: (s) => ['interested', 'accepted', 'rejected', 'declined', 'no_response_yet'].includes(s) },
  { key: 'confirmed', label: 'Confirmed', match: (s) => ['accepted', 'locked', 'approved', 'draft_submitted', 'revision_required'].includes(s) },
  { key: 'live',      label: 'Live',      match: (s) => ['live', 'posted'].includes(s) },
  { key: 'completed', label: 'Completed', match: (s) => s === 'completed' },
];

const fmtNum = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

export const CampaignProgressTab = ({ campaignId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAddress, setViewAddress] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [mandateFilter, setMandateFilter] = useState('all'); // 'all' or mandate_id

  const [mandates, setMandates] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/brand/campaigns/${campaignId}/progress`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const loadMandates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/brand/campaigns/${campaignId}/mandates`, { withCredentials: true });
      setMandates(data || []);
    } catch (e) {
      // Non-fatal
    }
  };

  useEffect(() => {
    if (campaignId) { load(); loadMandates(); }
    // eslint-disable-next-line
  }, [campaignId]);

  const rows = data?.creators || [];
  const roster = useMemo(
    () => rows.map((r) => ({ id: r.row_id, name: r.creator_name || '—' })),
    [rows],
  );

  // Mandate counts per mandate (#creators it applies to).
  const mandateCounts = useMemo(() => {
    const out = { all: rows.length };
    for (const m of mandates) {
      if (m.assigned_to === 'all') out[m.mandate_id] = rows.length;
      else out[m.mandate_id] = Array.isArray(m.assigned_to) ? m.assigned_to.length : 0;
    }
    return out;
  }, [mandates, rows.length]);

  // Status counts (recomputed from rows so chips show live numbers).
  const statusCounts = useMemo(() => {
    const out = { all: rows.length };
    for (const sf of STATUS_FILTERS.slice(1)) {
      out[sf.key] = rows.filter((r) => sf.match(r.status)).length;
    }
    return out;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const sf = STATUS_FILTERS.find((x) => x.key === statusFilter) || STATUS_FILTERS[0];
    let out = rows.filter((r) => sf.match(r.status));
    if (mandateFilter !== 'all') {
      const m = mandates.find((x) => x.mandate_id === mandateFilter);
      if (m) {
        if (m.assigned_to === 'all') {
          // keep all
        } else if (Array.isArray(m.assigned_to)) {
          out = out.filter((r) => m.assigned_to.includes(r.row_id));
        }
      }
    }
    return out;
  }, [rows, statusFilter, mandateFilter, mandates]);

  const exportCSV = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/brand/campaigns/${campaignId}/export-addresses`,
        { withCredentials: true, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data?.campaign_name || 'campaign').replace(/\s+/g, '_')}_addresses.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  if (loading) return (
    <div className="py-8 flex items-center justify-center text-[#6B6B6B] dark:text-zinc-500" data-testid="progress-loading">
      <Loader2 className="animate-spin mr-2" size={16} /> Loading progress…
    </div>
  );
  if (!data) return null;

  const s = data.summary || {};
  const addrCollected = s.addresses_collected || 0;

  return (
    <div className="space-y-4" data-testid="brand-campaign-progress-tab">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <SumTile label="Total"     value={s.total ?? 0}     color="#71717a" testid="progress-tile-total" />
        <SumTile label="Invited"   value={s.invited ?? 0}   color="#0055FF" testid="progress-tile-invited" />
        <SumTile label="Responded" value={s.responded ?? 0} color="#9B6EFF" testid="progress-tile-responded" />
        <SumTile label="Confirmed" value={s.confirmed ?? 0} color="#00C972" testid="progress-tile-confirmed" />
        <SumTile label={`Dispatched of ${s.total ?? 0}`} value={s.dispatched ?? 0} color="#0055FF" testid="progress-tile-dispatched" />
        <SumTile label={`Received of ${s.total ?? 0}`}   value={s.received ?? 0}   color="#00C972" testid="progress-tile-received" />
        <SumTile label="Live"      value={s.live ?? 0}      color="#FF451A" testid="progress-tile-live" />
        <SumTile label="Completed" value={s.completed ?? 0} color="#22C55E" testid="progress-tile-completed" />
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-1.5" data-testid="status-filter-bar">
        {STATUS_FILTERS.map((sf) => {
          const active = statusFilter === sf.key;
          const count = statusCounts[sf.key] ?? 0;
          return (
            <button
              key={sf.key}
              onClick={() => setStatusFilter(sf.key)}
              className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${active ? 'border-[#FF451A] bg-[#FF451A]/20 text-[#FF451A]' : 'border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
              data-testid={`status-chip-${sf.key}`}
            >
              {sf.label} <span className="opacity-60">· {count}</span>
            </button>
          );
        })}
      </div>

      {/* Mandate filter chips */}
      {mandates.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="mandate-filter-bar">
          <button
            onClick={() => setMandateFilter('all')}
            className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${mandateFilter === 'all' ? 'border-[#FF451A] bg-[#FF451A]/20 text-[#FF451A]' : 'border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
            data-testid="mandate-chip-all"
          >
            All Mandates <span className="opacity-60">· {rows.length}</span>
          </button>
          {mandates.map((m) => {
            const active = mandateFilter === m.mandate_id;
            const count = mandateCounts[m.mandate_id] ?? 0;
            return (
              <button
                key={m.mandate_id}
                onClick={() => setMandateFilter(m.mandate_id)}
                className={`px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${active ? 'border-[#FF451A] bg-[#FF451A]/20 text-[#FF451A]' : 'border-black/[0.10] dark:border-white/10 bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'}`}
                data-testid={`mandate-chip-${m.mandate_id}`}
                title={m.title}
              >
                {m.title.length > 22 ? `${m.title.slice(0, 22)}…` : m.title} <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Export CSV */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono">
          <span className="text-zinc-300">{addrCollected}</span> shipping address{addrCollected === 1 ? '' : 'es'} collected · showing {filteredRows.length} of {rows.length}
        </p>
        <Button
          onClick={exportCSV}
          disabled={!addrCollected}
          className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="progress-export-addresses-btn"
        >
          <Download size={13} className="mr-1.5" /> Export Addresses
        </Button>
      </div>

      {/* Creator status list */}
      {filteredRows.length === 0 ? (
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="progress-empty">
          <Users className="mx-auto mb-2 text-[#A1A1AA] dark:text-zinc-600" size={28} />
          {rows.length === 0 ? 'No creators assigned yet.' : 'No creators match the selected filters.'}
        </div>
      ) : (
        <div className="space-y-1.5">
          {filteredRows.map((r) => {
            const stageColor = STAGE_COLOR[r.status] || '#999';
            const deliv = r.deliverable_status ? DELIV_META[r.deliverable_status] : null;
            const sa = r.shipping_address;
            const collected = r.address_status === 'collected';
            const rowMandates = mandatesForCreator(mandates, r.row_id);
            return (
              <div key={r.row_id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3 flex items-center gap-3 flex-wrap" data-testid="brand-progress-row">
                {r.profile_photo_url ? (
                  <img src={r.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-xs font-bold shrink-0">
                    {r.creator_name?.[0]?.toUpperCase() || '·'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0F0F0F] dark:text-white truncate">{r.creator_name}</div>
                  <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                    {r.instagram_handle && <span>@{r.instagram_handle}</span>}
                    {r.instagram_followers > 0 && <span>· {fmtNum(r.instagram_followers)} followers</span>}
                    {(r.categories || []).slice(0, 2).map((cat) => (
                      <span key={cat} className="px-1.5 py-0.5 rounded-full bg-white/5 text-[#6B6B6B] dark:text-zinc-400 text-[10px]">{cat}</span>
                    ))}
                  </div>
                  {rowMandates.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid="row-mandate-tags">
                      {rowMandates.map((m) => <MandateTag key={m.mandate_id} mandate={m} />)}
                    </div>
                  )}
                </div>

                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0"
                  style={{ backgroundColor: `${stageColor}15`, color: stageColor }}
                  data-testid="progress-stage-chip"
                >
                  {r.status.replace(/_/g, ' ')}
                </span>

                {deliv && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0 inline-flex items-center gap-1"
                    style={{ backgroundColor: `${deliv.color}15`, color: deliv.color }}
                    data-testid="progress-deliv-chip"
                  >
                    <deliv.Icon size={9} /> {deliv.label}
                  </span>
                )}

                {r.source === 'bulk' && (
                  collected ? (
                    <button
                      onClick={() => setViewAddress({ row: r, sa })}
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0 inline-flex items-center gap-1 bg-[#00C972]/15 text-[#00C972] border border-[#00C972]/30 hover:bg-[#00C972]/25 transition-colors"
                      data-testid="progress-address-collected-chip"
                    >
                      <MapPin size={9} /> Address Collected
                    </button>
                  ) : (
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0 inline-flex items-center gap-1 bg-[#FFB800]/15 text-[#FFB800] border border-[#FFB800]/30"
                      data-testid="progress-address-pending-chip"
                    >
                      <MapPin size={9} /> Address Pending
                    </span>
                  )
                )}

                {/* Dispatch Tracker chip — read-only for brand. Bulk rows use
                    the CM's dispatch_status; exclusive-flow rows have no
                    logistics stage, so we render a grey "N/A" chip so the
                    brand still sees a consistent column. */}
                {r.source === 'bulk' && r.dispatch_status && DISPATCH_META[r.dispatch_status] && (() => {
                  const dm = DISPATCH_META[r.dispatch_status];
                  return (
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0 inline-flex items-center gap-1"
                      style={{ backgroundColor: `${dm.color}15`, color: dm.color, border: `1px solid ${dm.color}30` }}
                      data-testid={`progress-dispatch-chip-${r.dispatch_status}`}
                    >
                      <PackageIcon size={9} /> {dm.label}
                    </span>
                  );
                })()}
                {r.source === 'exclusive' && (() => {
                  const dm = DISPATCH_META.not_applicable;
                  return (
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider font-mono shrink-0 inline-flex items-center gap-1"
                      style={{ backgroundColor: `${dm.color}15`, color: dm.color, border: `1px solid ${dm.color}30` }}
                      data-testid="progress-dispatch-chip-not_applicable"
                    >
                      <PackageIcon size={9} /> {dm.label}
                    </span>
                  );
                })()}

                {r.last_updated && (
                  <span className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 font-mono shrink-0" data-testid="progress-last-updated">
                    {String(r.last_updated).slice(0, 10)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mandates section (read+write for brand) */}
      <div className="pt-4 border-t border-black/[0.08] dark:border-white/5">
        <MandatesPanel
          campaignId={campaignId}
          mandates={mandates}
          onChange={loadMandates}
          roster={roster}
        />
      </div>

      {/* Campaign Completion Report (read-only for brand; PDF + Excel download) */}
      <BrandCampaignReportPanel campaignId={campaignId} />

      {/* Read-only address viewer */}
      <Dialog open={!!viewAddress} onOpenChange={(o) => { if (!o) setViewAddress(null); }}>
        <DialogContent className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white max-w-md" data-testid="brand-address-viewer">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <MapPin size={16} className="text-[#FF451A]" /> Shipping Address
            </DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
          </DialogHeader>
          {viewAddress?.sa && (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono uppercase tracking-wider">For {viewAddress.row.creator_name}</p>
              <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg p-4 leading-relaxed font-mono text-zinc-200">
                <div className="text-[#0F0F0F] dark:text-white font-semibold">{viewAddress.sa.full_name}</div>
                <div>{viewAddress.sa.phone}</div>
                <div className="mt-2">{viewAddress.sa.address_line_1}</div>
                {viewAddress.sa.address_line_2 && <div>{viewAddress.sa.address_line_2}</div>}
                <div>{viewAddress.sa.city}, {viewAddress.sa.state} {viewAddress.sa.pincode}</div>
              </div>
              <p className="text-[10px] text-[#A1A1AA] dark:text-zinc-600 italic">Read-only — addresses are collected and edited by your Campaign Manager.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SumTile = ({ label, value, color, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">{label}</div>
    <div className="text-2xl font-bold font-mono" style={{ color }}>{value}</div>
  </div>
);
